// src/components/ui/CreateClientModal.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { auth } from "../../firebase-config";
import { useToast } from "./ToastContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import ButtonComponent from "./ButtonComponent";
import TextInputComponent from "./TextInputComponent";

const CREATE_CLIENT_URL = "https://createnewclient-774073159618.us-central1.run.app";

const normalizeDigits = (s) => (s || "").toString().replace(/\D/g, "");

const CreateClientModal = ({ open, onClose, onClientCreated, initialName }) => { // NEW PROP
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const [clientData, setClientData] = useState({
    fullName: "",
    email: "",
    phone: "",
  });

  // Reset when opened and apply initialName
  useEffect(() => {
    if (open) {
      setClientData({ 
        fullName: initialName || "", // Pre-fill name
        email: "", 
        phone: "" 
      });
      setIsLoading(false);
    }
  }, [open, initialName]);

  const canSubmit = useMemo(() => {
    const fullName = (clientData.fullName || "").trim();
    const email = (clientData.email || "").trim();
    return fullName.length > 0 && email.length > 0 && !isLoading;
  }, [clientData, isLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose && onClose();
  };

  const handleCreateClient = async () => {
    const fullName = (clientData.fullName || "").trim();
    const email = (clientData.email || "").trim();
    const phone = (clientData.phone || "").trim();

    if (!fullName || !email) {
      addToast("Full name and email are required.", "warning");
      return;
    }

    setIsLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const payload = {
        fullName,
        email,
        phone: phone ? normalizeDigits(phone) : "",
      };

      const response = await axios.post(CREATE_CLIENT_URL, payload, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      addToast("Client created successfully!", "success");

      if (typeof onClientCreated === "function") {
        onClientCreated(response.data.client);
      }

      handleClose();
    } catch (error) {
      console.error("Failed to create client:", error);
      addToast(error.response?.data?.error || "Failed to create client", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && canSubmit) {
      e.preventDefault();
      handleCreateClient();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ "& .MuiDialog-paper": { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1.25 }}>Create New Client</DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: "16px 24px" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the client’s details. Fields marked with * are required.
          </Typography>

          <Box
            component="form"
            noValidate
            autoComplete="off"
            onKeyDown={onKeyDown}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <TextInputComponent
              label="Full Name *"
              name="fullName"
              value={clientData.fullName}
              onChange={handleChange}
              readOnly={isLoading}
            />

            <TextInputComponent
              label="Email Address *"
              name="email"
              type="email"
              value={clientData.email}
              onChange={handleChange}
              readOnly={isLoading}
            />

            <TextInputComponent
              label="Phone Number"
              name="phone"
              type="tel"
              value={clientData.phone}
              onChange={handleChange}
              readOnly={isLoading}
              placeholder="(555) 123-4567"
            />

            <Divider sx={{ mt: 0.5 }} />

            <Typography variant="caption" color="text.secondary">
              Tip: You can create the client now and assign documents/forms immediately after.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <ButtonComponent
          label="Cancel"
          type="secondary"
          onClick={handleClose}
          disabled={isLoading}
        />
        <ButtonComponent
          label="Create Client"
          type="primary"
          onClick={handleCreateClient}
          isLoading={isLoading}
          disabled={!canSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CreateClientModal;